<script>
    import moment from "moment";
    import BaseTab from "@/tabs/mixins/BaseTab.js";
    import { mapGetters } from "vuex";
    import tabIcon from "@/assets/narvarostatistik.svg";
    import KFilter from "@/components/KFilter/KFilter.vue";
    import { generateSchema } from "@/components/forms/Form/schemaHelpers";
    import ActionIconOpanmalan from "@/assets/actionIcons/show.svg";
    import NarvarostatistikTableRow from "@/tabs/Skreg/Statistik/NarvarostatistikTableRow.vue";
    import JournalAnteckning from "@/tabs/Journalanteckningar/Journalanteckning.vue";
    import ModalBasic, { BasicModalVariant } from "@/components/generic/ModalBasic.vue";

    import { fetchData } from "@/tabs/Operation/utils";

    import IdrottPopup from "@/tabs/Skreg/Statistik/IdrottPopup.vue";
    import KHeader from "@/components/KHeader.vue";
    import { NARVARO_HEADERS, returnValueIfNotUndefined } from "@/tabs/Skreg/Statistik/narvarostatistikConstants.js";

    export default {
        extends: BaseTab,
        name: "NarvarostatistikSpelare",
        tabName: "Närvarostatistik spelare",
        icon: tabIcon,
        patientBound: true,

        components: {
            KFilter,
            NarvarostatistikTableRow,
            ModalBasic,
            IdrottPopup,
            KHeader,
        },

        props: {
            parameters: {
                type: Object,
            },
            index: {
                type: Number,
            },
        },

        data() {
            return {
                searchFilterValues: null,
                defaultSearchValues: null,
                headers: NARVARO_HEADERS,
                dropdownActions: [
                    {
                        title: "Journalanteckningar skadekort",
                        name: "journalanteckningarskadekort",
                        icon: ActionIconOpanmalan,
                        show: (item) => !!item.data.sista_oavstamd_skadekort,
                    },
                ],
                rowData: {},
                idrottTotal: 0,
                idrott: null,
                idrottId: null,
                spelare: null,
                showNoDataMessage: false,
            };
        },

        computed: {
            ...mapGetters("patientData", ["patient"]),

            filterSchema() {
                return generateSchema({
                    addToAll: { ignoreEmptyData: true },
                    schema: [
                        {
                            component: "LagAutocomplete",
                            name: "lag",
                            label: "Lag",
                            idrottId: this.idrottId,
                            spelareId: this.spelare?.id,
                            includeAllOption: true,
                            placeholder: "Välj lag",
                            validation: "required",
                            autoPosition: true,
                            tagValueFormatter: (value) =>
                                value === "allalag"
                                    ? "Alla lag"
                                    : (this.$store.getters["skreg/lag/lagById"]({ id: value })?.namn ?? value),
                        },
                        {
                            component: "KDateTimepicker",
                            name: "fromDate",
                            label: "Från datum",
                            placeholder: "Välj datum",
                            validation: "required",
                        },
                        {
                            component: "KDateTimepicker",
                            name: "toDate",
                            label: "Till datum",
                            placeholder: "Välj datum",
                            validation: "required",
                        },
                    ],
                });
            },
        },

        methods: {
            async onSearchSubmit({ formData }) {
                this.searchFilterValues = formData;
                await this.searchLag();
            },

            async dropdownaction({ action, item }) {
                switch (action) {
                    case "journalanteckningarskadekort": {
                        let id = item.data.sista_oavstamd_skadekort;
                        await this.$store.dispatch("tabs/closeTab", JournalAnteckning, { root: true });
                        await this.$store.dispatch("journalanteckningar/getJournalanteckning", id);
                        let anteckning = this.$store.getters["journalanteckningar/anteckning"];
                        await this.$store.dispatch("patientData/loadFromBackend", { id: anteckning.patient.id });
                        await this.$store.dispatch("tabs/openTab", { component: JournalAnteckning });
                        break;
                    }
                    default:
                        break;
                }
            },
            async searchLag() {
                await this.$store.dispatch("tabs/displaySpinner", { id: this.tabId, display: true });
                let url_query_parameters = `?lag=${this.returnValueIfNotUndefined(
                    this.searchFilterValues?.lag
                )}&tidpunkt__date__gte=${this.returnValueIfNotUndefined(
                    this.searchFilterValues?.fromDate
                )}&tidpunkt__date__lte=${this.returnValueIfNotUndefined(this.searchFilterValues?.toDate)}`;
                await this.getSpelareData(url_query_parameters);
                this.showNoData();
                await this.$store.dispatch("tabs/displaySpinner", { id: this.tabId, display: false });
            },
            async chooseIdrott(idrottId) {
                this.idrottId = idrottId;
                this.idrott = this.$store.getters["skreg/idrott/idrottById"]({ id: idrottId })?.namn ?? idrottId;
                let url_query_parameters = `?lag=${this.returnValueIfNotUndefined(
                    this.searchFilterValues?.lag
                )}&tidpunkt__date__gte=${this.returnValueIfNotUndefined(
                    this.searchFilterValues?.fromDate
                )}&tidpunkt__date__lte=${this.returnValueIfNotUndefined(this.searchFilterValues?.toDate)}`;
                await this.getSpelareData(url_query_parameters);
            },
            returnValueIfNotUndefined,

            async fetchSpelare() {
                this.spelare = await fetchData({
                    url: `skreg/spelare/${this.patient.id}`,
                });
                if (this.spelare.spelarelag?.length > 0) {
                    const { subsetName, total } = await this.$store.dispatch("skreg/idrott/fetchIdrott", {
                        filterParams: { spelare: this.spelare.id },
                    });
                    this.idrottTotal = total ?? 0;
                    if (this.idrottTotal === 1) {
                        const [idrott] = this.$store.getters["skreg/idrott/idrottSubset"]({ subsetName, limit: 0 });
                        this.idrottId = idrott.id;
                        this.idrott = idrott.namn;
                    }
                } else {
                    await this.$refs.dialog.show({
                        variant: BasicModalVariant.WARNING,
                        title: "Spelaren är inte inlagd i något lag.",
                        okButton: "OK",
                    });
                    this.$store.dispatch("tabs/closeTab", this.tabId);
                }
            },
            async getSpelareData(query_url) {
                this.rowData = await fetchData({
                    url:
                        `/skreg/statistik/idrotter/${(this.idrott ?? "").toLowerCase()}/narvaro/spelare/${
                            this.spelare.id
                        }/` + this.returnValueIfNotUndefined(query_url),
                });
                if (this.searchFilterValues.lag !== "allalag") {
                    this.rowData.spelare.spelarelag = this.rowData.spelare.spelarelag.filter(
                        (item) => item.lag === this.searchFilterValues.lag
                    );
                }
            },
            showNoData() {
                this.showNoDataMessage = !(this.rowData?.data && Object.keys(this.rowData.data).length > 0);
            },
        },
        async created() {
            const firstDate = moment().startOf("month");
            const lastDate = moment().endOf("month");
            this.defaultSearchValues = {
                fromDate: firstDate.format("YYYY-MM-DD"),
                toDate: lastDate.format("YYYY-MM-DD"),
            };
            this.searchFilterValues = { ...this.defaultSearchValues };
            await this.fetchSpelare();
        },
    };
</script>

<template>
    <div class="narvarostatistik-spelare">
        <KHeader heading="Närvarostatistik spelare" />
        <KFilter
            v-if="spelare"
            name="narvaro-spelare-filter"
            :schema="filterSchema"
            :defaultValue="defaultSearchValues"
            :expanded="true"
            @submit="onSearchSubmit"
            hideSearch
        />
        <div v-if="!showNoDataMessage" class="antal_aktiviteter_deltagande">
            <h4>
                Antal förekomster av partiellt deltagande:
                <span>{{ rowData.data?.antal_aktiviteter_med_delvis_deltagande }}</span>
            </h4>
            <table>
                <thead class="header">
                    <tr>
                        <th class="" v-for="(header, index) in headers" :key="index">
                            <div
                                v-if="header === 'Deltagare'"
                                class="header-vertical-padding-8align-left heading-style padding-left-20"
                            >
                                {{ header }}
                            </div>
                            <div v-else class="header-vertical-padding-8 heading-style align-right">
                                {{ header }}
                            </div>
                        </th>
                    </tr>
                </thead>
                <NarvarostatistikTableRow
                    :rowData="rowData"
                    :index="index"
                    :dropdownActions="dropdownActions"
                    :isAllNestedRowsShown="true"
                    @action="dropdownaction"
                />
            </table>
        </div>
        <div v-else class="no-data">
            <img src="@/assets/check_circle_black.svg" alt="black circle" /> Det finns inga avstämda aktiviteter i vald
            tidsperiod.
        </div>
        <IdrottPopup v-if="idrottTotal > 1" variant="form" :spelareId="spelare?.id" @chooseIdrott="chooseIdrott" />
        <modal-basic ref="dialog" />
    </div>
</template>

<style lang="scss" scoped>
    @import "@/style/variables";

    .narvarostatistik-spelare {
        .no-data {
            img {
                height: 20px;
                margin-right: 10px;
            }

            display: flex;
        }

        .antal_aktiviteter_deltagande {
            margin: 2rem 0;

            h4 {
                margin: 0;
            }
        }

        .padding-left-20 {
            padding-left: 1.25rem;
        }

        .align-right {
            text-align: right;
        }

        .header-vertical-padding-8 {
            padding-top: 0.5rem;
            padding-bottom: 0.5rem;
        }

        table {
            border-collapse: collapse;
            width: 100%;

            thead {
                tr {
                    color: $color-text-black;
                    line-height: 1rem;
                    font-size: 14px;
                }

                background-color: $color-bg-table-header;

                .heading-style {
                    color: $color-table-header;
                    font-family: Roboto Medium;
                    font-weight: 500;
                    line-height: 17px;
                    font-size: 14px;
                }
            }
        }
    }
</style>
